import { useEffect, useRef } from 'react';
import ApiService, { ICountryData } from '../services/api';
import { cookieHelpers } from '../utils/cookieUtils';

// Country to language mapping
const defaultLanguage: Record<string, string> = {
  "Albania": "Albanian",
  "Austria": "German",
  "Belgium": "Dutch",
  "Bosnia-Herzegovina": "Bosnian",
  "Botswana": "English",
  "Bulgaria": "Bulgarian",
  "Croatia": "Croatian",
  "Cyprus": "Greek",
  "Czech Republic": "Czech",
  "Denmark": "Danish",
  "Estonia": "Estonian",
  "Finland": "Finnish",
  "France": "French",
  "French Polynesia": "French",
  "Germany": "German",
  "Ghana": "English",
  "Greece": "Greek",
  "Hungary": "Hungarian",
  "Iceland": "Icelandic",
  "Ireland": "English",
  "Israel": "Hebrew",
  "Italy": "Italian",
  "Kosovo": "Albanian",
  "Latvia": "Latvian",
  "Lesotho": "English",
  "Lithuania": "Lithuanian",
  "Macedonia": "Macedonian",
  "Malta": "English",
  "Moldova": "Romanian",
  "Montenegro": "Montenegrin",
  "Namibia": "English",
  "Netherlands": "Dutch",
  "North Macedonia": "Macedonian",
  "Norway": "Norwegian",
  "Poland": "Polish",
  "Portugal": "Portuguese",
  "Romania": "Romanian",
  "Serbia": "Serbian",
  "Slovak Republic": "Slovak",
  "Slovakia": "Slovak",
  "Slovenia": "Slovene",
  "South Africa": "English",
  "Spain": "Spanish",
  "Swaziland": "English",
  "Eswatini": "English",
  "Sweden": "Swedish",
  "Switzerland": "German",
  "Turkey": "Turkish",
  "Ukraine": "Ukrainian",
  "United Kingdom": "English",
};

interface UseIpLanguageDetectionProps {
  languages: ICountryData[];
  loading: boolean;
  flowType: 'distributor' | 'customer';
  onLanguageDetected: (languageCode: string) => Promise<void>;
  onLanguageSet: (languageCode: string) => void;
}

export const useIpLanguageDetection = ({
  languages,
  loading,
  flowType,
  onLanguageDetected,
  onLanguageSet,
}: UseIpLanguageDetectionProps) => {
  const ipDetectionAttempted = useRef<boolean>(false);
  const apiService = useRef(new ApiService());

  useEffect(() => {
    // Early return if already attempted
    if (ipDetectionAttempted.current) {
      return;
    }

    // Skip IP detection when the user has already chosen a language (including English)
    if (cookieHelpers.hasLanguage(flowType)) {
      return;
    }

    if (languages.length === 0) {
      return;
    }

    if (loading) {
      return;
    }
    
    if (languages.length > 0 && !loading) {
      ipDetectionAttempted.current = true;
      
      // Capture current values to avoid stale closures
      const currentLanguages = languages;
      
      const detectLanguageFromIp = async () => {
        try {
          // Add timeout to prevent long waits
          const timeoutPromise = new Promise<null>((resolve) => {
            setTimeout(() => resolve(null), 3000); // 3 second timeout
          });

          const countryNamePromise = apiService.current.getCountryNameFromIp();
          const countryName = await Promise.race([countryNamePromise, timeoutPromise]);
          
          if (!countryName) {
            // Fallback to English on timeout
            const englishLanguage = currentLanguages.find(
              (lang) => lang.country_language?.toLowerCase() === 'english' || lang.country_language_code === 'en'
            );
            
            if (englishLanguage && englishLanguage.country_language_code) {
              onLanguageSet(englishLanguage.country_language_code);
              await onLanguageDetected(englishLanguage.country_language_code);
            } else {
              onLanguageSet('en');
              await onLanguageDetected('en');
            }
            return;
          }
          
          
          if (countryName) {
            // Normalize country name - handle variations
            let normalizedCountryName = countryName.trim();
            
            // Remove "The" prefix if present (e.g., "The Netherlands" -> "Netherlands")
            if (normalizedCountryName.toLowerCase().startsWith('the ')) {
              normalizedCountryName = normalizedCountryName.substring(4).trim();
            }
            
            
            // Try exact match first
            let targetLanguage = defaultLanguage[normalizedCountryName];
            
            // If no exact match, try common variations
            if (!targetLanguage) {
              const countryVariations: Record<string, string> = {
                'UK': 'United Kingdom',
                'Great Britain': 'United Kingdom',
                'GB': 'United Kingdom',
                'The Netherlands': 'Netherlands',
                'The United Kingdom': 'United Kingdom',
              };
              
              const mappedCountry = countryVariations[countryName.trim()] || countryVariations[normalizedCountryName];
              if (mappedCountry) {
                targetLanguage = defaultLanguage[mappedCountry];
              }
            }
            
            if (targetLanguage) {
              // Find matching language in the languages array - try multiple matching strategies
              let matchedLanguage = currentLanguages.find(
                (lang) => lang.country_language?.toLowerCase() === targetLanguage.toLowerCase()
              );

              
              // If not found, try matching by language code (e.g., "en" for English)
              if (!matchedLanguage && targetLanguage.toLowerCase() === 'english') {
                matchedLanguage = currentLanguages.find(
                  (lang) => lang.country_language_code?.toLowerCase() === 'en'
                );
              }
              
              if (matchedLanguage && matchedLanguage.country_language_code) {
                onLanguageSet(matchedLanguage.country_language_code);
                await onLanguageDetected(matchedLanguage.country_language_code);
                return;
              } 
            } 
          }
          
          // Only fallback to English if IP detection completely fails (no country detected or error)
          // If country was detected but language not found, don't set any language
          if (!countryName) {
            // No country detected - fallback to English
            const englishLanguage = currentLanguages.find(
              (lang) => lang.country_language?.toLowerCase() === 'english' || lang.country_language_code === 'en'
            );
            
            if (englishLanguage && englishLanguage.country_language_code) {
              onLanguageSet(englishLanguage.country_language_code);
              await onLanguageDetected(englishLanguage.country_language_code);
            } else {
              onLanguageSet('en');
              await onLanguageDetected('en');
            }
          }
        } catch (error) {
          console.error('[useIpLanguageDetection] Error detecting language from IP:', error);
          // Fallback to English only on error
          const englishLanguage = currentLanguages.find(
            (lang) => lang.country_language?.toLowerCase() === 'english' || lang.country_language_code === 'en'
          );
          
          if (englishLanguage && englishLanguage.country_language_code) {
            onLanguageSet(englishLanguage.country_language_code);
            await onLanguageDetected(englishLanguage.country_language_code);
          } else {
            onLanguageSet('en');
            await onLanguageDetected('en');
          }
        }
      };
      
      detectLanguageFromIp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages.length, loading]);
};

